<template>
  <Label
    :as="props.for ? 'label' : 'div'"
    :for="props.for"
    :class="cn(
      'flex items-center gap-1.5 font-ds text-xs font-bold uppercase tracking-ds-caps text-muted-foreground',
      props.class,
    )"
  >
    <slot>{{ label }}</slot>

    <!--
      The mark is the field's status said in one glyph. The four are a closed
      vocabulary so a screen can print a legend for them: amber dot = owed,
      Iris sparkle = filled for you, help glyph = a default you may change,
      the word `optional` = neither. Add a fifth here and every legend in the
      app is quietly wrong; use the slot for a one-off instead.
    -->
    <slot name="mark">
      <span
        v-if="mark === 'required'"
        class="size-1.5 shrink-0 rounded-full bg-warning"
      />
      <CircleHelp
        v-else-if="mark === 'default'"
        class="size-3 shrink-0"
      />
      <Sparkles
        v-else-if="mark === 'ai'"
        class="size-3 shrink-0 text-brand"
      />
      <span
        v-else-if="mark === 'optional'"
        class="text-xs font-medium normal-case tracking-normal"
      >
        optional
      </span>
    </slot>
  </Label>
</template>

<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { CircleHelp, Sparkles } from '@lucide/vue'
import { cn } from '@ds/lib/utils'

import { Label } from '../../primitives/label'

import type { FieldLabelMark } from './index'

/*
 * A field's caption, plus an optional one-glyph mark for where its value stands.
 *
 * A pattern rather than a primitive: it is Label + a mark vocabulary, and the
 * vocabulary is a product decision. It carries no bottom margin — the gap to the
 * control belongs to the form that lays them out, so pass `class="mb-1.5"` (or
 * whatever that form uses) rather than assuming one here.
 *
 * Pass `for` to render a real `<label>` bound to a control; without it this is a
 * `<div>`, which is what a caption over a composite (a radio group, a picker,
 * a read-only value) should be — a label pointing at nothing is worse for a
 * screen reader than no label element at all. Either way the element is Reka's
 * Label, so double-clicking the caption selects the control's text rather than
 * selecting the caption.
 */
const props = defineProps<{
  /** The caption. Or use the default slot for anything richer than a string. */
  label?: string
  mark?: FieldLabelMark
  /** `id` of the control this labels. Set it and the element becomes `<label>`. */
  for?: string
  class?: HTMLAttributes['class']
}>()
</script>
